import type { BaseFreightGroup, BaseFreightRate, CreateBaseFreightRateRequest, Ratebook } from '@api/useFreight/types'
import { useRateManagement } from '@api/useFreight/useFreight'
import { message } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { type MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import type { MileageBand } from '../types'

interface UseMileageBandDetailsProps {
  selectedGroup: BaseFreightGroup
  ratebook: Ratebook
  isEditMode: boolean
  setIsEditMode: (isEditing: boolean) => void
}

export interface CreateRateFormValues {
  create_mode: 'copy' | 'new'
  copy_from_date_id?: string
  effective_date: dayjs.Dayjs
  effective_to?: dayjs.Dayjs | null
}

export function useMileageBandDetails({
  selectedGroup,
  ratebook,
  isEditMode,
  setIsEditMode,
}: UseMileageBandDetailsProps) {
  const queryClient = useQueryClient()

  const [isCreatePopoverOpen, setIsCreatePopoverOpen] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)

  const { useCreateBaseFreight, useDeleteBaseFreightRate, useUpdateBaseFreight } = useRateManagement()
  const createRateMutation = useCreateBaseFreight()
  const deleteRateMutation = useDeleteBaseFreightRate()
  const updateRateMutation = useUpdateBaseFreight()
  const [selectedRate, setSelectedRate] = useState<BaseFreightRate | null>(null)
  const [isEffectiveDateModalOpen, setIsEffectiveDateModalOpen] = useState(false)
  const dirtyGridApiRef = useRef<MutableRefObject<unknown>>(null)
  const [editingCopyRows, setEditingCopyRows] = useState<MileageBand[]>([])
  const [isEditSavePopoverOpen, setIsEditSavePopoverOpen] = useState(false)
  const [isChangeEffectivePopoverOpen, setIsChangeEffectivePopoverOpen] = useState(false)
  const [hasDuplicateMiles, setHasDuplicateMiles] = useState(false)
  const [hasInvalidMileValues, setHasInvalidMileValues] = useState(false)

  // Get the rate history for the selected group
  const sortedHistory = useMemo(() => {
    if (!selectedGroup) return []

    return [...selectedGroup.rows].sort((a, b) => dayjs(b.effective_from).valueOf() - dayjs(a.effective_from).valueOf())
  }, [selectedGroup])

  // Update useEffect to handle group changes and auto-select active rate
  useEffect(() => {
    if (selectedGroup?.rows?.length > 0) {
      // Check if the currently selected rate still exists in the updated group
      const currentRateExists =
        selectedRate && selectedGroup.rows.some((rate: BaseFreightRate) => rate.id === selectedRate.id)

      if (currentRateExists) {
        // If it exists, find the updated version and re-select it
        const updatedSelectedRate = selectedGroup.rows.find((rate: BaseFreightRate) => rate.id === selectedRate.id)
        setSelectedRate(updatedSelectedRate || null) // Fallback to null just in case
      } else {
        // If the previous selection doesn't exist, find the active rate or the most recent rate
        const activeRate =
          selectedGroup.rows.find((rate: BaseFreightRate) => rate.status === 'Active') ||
          [...selectedGroup.rows].sort(
            (a: BaseFreightRate, b: BaseFreightRate) =>
              dayjs(b.effective_from).valueOf() - dayjs(a.effective_from).valueOf()
          )[0]
        setSelectedRate(activeRate)
      }
    } else {
      setSelectedRate(null)
    }
  }, [selectedGroup, selectedRate]) // Only depend on selectedGroup

  // Update useEffect for rate changes
  useEffect(() => {
    if (selectedRate?.rates) {
      // Instead of using mapRatesToMileRanges, directly use the rates from the selected rate
      setEditingCopyRows(
        selectedRate.rates.map((rate) => ({
          key: `${rate.from_mile}-${rate.to_mile}`,
          from_mile: rate.from_mile,
          to_mile: rate.to_mile,
          rate: rate.rate,
        }))
      )
    }
  }, [selectedRate])

  const handleCreateRate = async (values: CreateRateFormValues) => {
    try {
      if (!selectedGroup || !selectedRate) return

      let ratesToUse: MileageBand[] = []
      if (values.create_mode === 'copy' && values.copy_from_date_id) {
        // Find the rate to copy from
        const rateToCopy = sortedHistory.find((rate) => rate.id === values.copy_from_date_id)
        if (rateToCopy) {
          // Directly use the rates from the source, preserving all mileage bands
          ratesToUse = rateToCopy.rates.map((rate: MileageBand) => ({
            from_mile: rate.from_mile,
            to_mile: rate.to_mile,
            rate: rate.rate,
          }))
        }
      } else {
        // If starting from scratch, use default ranges (0-200)
        ratesToUse = Array.from({ length: 40 }, (_, i) => ({
          from_mile: i * 5,
          to_mile: (i + 1) * 5,
          rate: 0,
        }))
      }

      const formattedData = {
        base_freight_id: `${ratebook.counterparty_id}_${selectedGroup.freight_region_id || 'global'}_${Date.now()}`,
        effective_from: values.effective_date.format('YYYY-MM-DD'),
        effective_to: values.effective_to?.format('YYYY-MM-DD') || null,
        book_type: ratebook.book_type,
        counterparty: ratebook.counterparty_name,
        counterparty_id: ratebook.counterparty_id,
        unit: (selectedRate.unit as 'Per Gallon' | 'Per Truck') || 'Per Gallon',
        freight_region_name: selectedGroup.freight_region_name,
        freight_region_id: selectedGroup.freight_region_id,
        region: selectedGroup.freight_region_name,
        region_id: selectedGroup.freight_region_id,
        product_group: selectedGroup.product_group || null,
        product_group_id: selectedRate.product_group || null,
        rates: ratesToUse,
      }

      setIsCreatePopoverOpen(false)
      await createRateMutation.mutateAsync(formattedData as CreateBaseFreightRateRequest)
      await queryClient.invalidateQueries({
        queryKey: ['truck_rate/overview'],
      })
      message.success('Rate scheduled successfully')
    } catch (error) {
      message.error('Failed to schedule rate')
    }
  }

  const handleDeleteRate = async (rateId: string) => {
    try {
      await deleteRateMutation.mutateAsync({ id: rateId })
      await queryClient.invalidateQueries({
        queryKey: ['truck_rate/overview'],
      })
      setSelectedRate(null)

      message.success('Rate deleted successfully')
    } catch (error) {
      message.error('Failed to delete rate')
    }
  }

  const saveRateChanges = async (effectiveDate?: string) => {
    if (!effectiveDate) {
      // No longer need to open a popover here, directly proceed with save
      // using the selectedRate's effective_from date.
      if (!selectedRate) {
        message.error('No rate selected to save changes for.')
        return
      }
      // Directly use the selectedRate's effective dates
      const rateEffectiveFrom = selectedRate.effective_from
      const rateEffectiveTo = selectedRate.effective_to || null

      // Format for the API call
      const effectiveDateInfo = JSON.stringify({
        effective_from: rateEffectiveFrom,
        effective_to: rateEffectiveTo,
      })
      await executeSaveChanges(effectiveDateInfo)
    } else {
      // This branch is now only used by the 'Change Effective' popover confirmation
      await executeSaveChanges(effectiveDate)
    }
  }

  // Extracted the core save logic to avoid duplication
  const executeSaveChanges = async (effectiveDateInfo: string) => {
    setSaveLoading(true)
    try {
      if (!selectedRate || !selectedGroup) {
        setSaveLoading(false)
        return
      }

      // Parse the effective date info
      let parsedEffectiveDate: string
      let effectiveTo: string | null = null

      // If effectiveDate is a JSON string containing both dates
      if (effectiveDateInfo.startsWith('{')) {
        const dates = JSON.parse(effectiveDateInfo)
        parsedEffectiveDate = dayjs(dates.effective_from).format('YYYY-MM-DD')
        effectiveTo = dates.effective_to ? dayjs(dates.effective_to).format('YYYY-MM-DD') : null
      } else {
        // Handle case where only effective_from is provided (likely an ISO string)
        parsedEffectiveDate = dayjs(effectiveDateInfo).format('YYYY-MM-DD')
      }

      // Use the current editingCopyRows which has all edited rows
      const updatedRates = editingCopyRows.map((row) => ({
        from_mile: Number(row.from_mile),
        to_mile: Number(row.to_mile),
        rate: Number(row.rate),
      }))

      const formattedData = {
        truck_rate_id: selectedRate.id,
        rates: updatedRates,
        effective_from: parsedEffectiveDate,
        effective_to: effectiveTo,
      }

      const updatedRate = await updateRateMutation.mutateAsync(formattedData)

      await queryClient.invalidateQueries({
        queryKey: ['truck_rate/overview'],
      })

      setSelectedRate((prev) => ({
        ...prev,
        ...updatedRate, // Update with the full response if needed, or just the parts that changed
        effective_from: parsedEffectiveDate,
        effective_to: effectiveTo,
        rates: updatedRates,
      }))

      setIsEditMode(false)
      setIsEditSavePopoverOpen(false) // Close edit save popover if it was somehow open

      message.success('Rate updated successfully')
    } catch (error) {
      message.error('Failed to update rate')
      console.error('Error saving rate changes:', error)
    } finally {
      setSaveLoading(false)
    }
  }

  const handleUpdateEffectiveDate = async (rateToUpdate: BaseFreightRate, dateInfo: string) => {
    setSaveLoading(true) // Reuse save loading state or create a new one if needed
    try {
      let effective_from: string
      let effective_to: string | null = null

      if (dateInfo.startsWith('{')) {
        const dates = JSON.parse(dateInfo)
        effective_from = dayjs(dates.effective_from).format('YYYY-MM-DD')
        effective_to = dates.effective_to ? dayjs(dates.effective_to).format('YYYY-MM-DD') : null
      } else {
        effective_from = dayjs(dateInfo).format('YYYY-MM-DD')
      }

      // Use existing rates from the rateToUpdate
      const existingRates = rateToUpdate.rates.map((r) => ({
        from_mile: r.from_mile,
        to_mile: r.to_mile,
        rate: r.rate,
      }))

      const formattedData = {
        truck_rate_id: rateToUpdate.id,
        rates: existingRates, // Send original rates
        effective_from, // Send new effective_from
        effective_to, // Send new effective_to
      }

      await updateRateMutation.mutateAsync(formattedData)
      await queryClient.invalidateQueries({
        queryKey: ['truck_rate/overview'],
      })

      // Update the selected rate if it's the one being changed
      if (selectedRate?.id === rateToUpdate.id) {
        setSelectedRate((prev) =>
          prev
            ? {
                ...prev,
                effective_from,
                effective_to,
              }
            : null
        )
      }

      message.success('Effective date updated successfully')
    } catch (error) {
      message.error('Failed to update effective date')
      console.error('Error updating effective date:', error)
    } finally {
      setSaveLoading(false)
      setIsChangeEffectivePopoverOpen(false) // Ensure popover closes
    }
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    setIsEditSavePopoverOpen(false)
    // Reset editingCopyRows to the current selected rate's rates
    if (selectedRate?.rates) {
      setEditingCopyRows(
        selectedRate.rates.map((rate) => ({
          key: `${rate.from_mile}-${rate.to_mile}`,
          from_mile: rate.from_mile,
          to_mile: rate.to_mile,
          rate: rate.rate,
        }))
      )
    }
  }

  return {
    isCreatePopoverOpen,
    setIsCreatePopoverOpen,
    saveLoading,
    selectedRate,
    setSelectedRate,
    isEffectiveDateModalOpen,
    setIsEffectiveDateModalOpen,
    dirtyGridApiRef,
    editingCopyRows,
    setEditingCopyRows,
    isEditSavePopoverOpen,
    setIsEditSavePopoverOpen,
    isChangeEffectivePopoverOpen,
    setIsChangeEffectivePopoverOpen,
    hasDuplicateMiles,
    setHasDuplicateMiles,
    hasInvalidMileValues,
    setHasInvalidMileValues,
    sortedHistory,
    handleCreateRate,
    handleDeleteRate,
    saveRateChanges,
    handleUpdateEffectiveDate,
    handleCancelEdit,
  }
}
